import { useEffect, useState } from "react";
import FileReviewIcon from "../../assets/documentPageAssets/fileReviewScreen.png";
// Shared with GlobalDocumentPreviewModal's apply handlers, so rendering and
// applying cannot disagree about what counts as a Dreams suggestion.
import { isDreamsSuggestion, actionOf } from "../../utils/policyFormatters.js";

// Document Preview Modal — opened from Issues modal "Review & Update"
const DocumentPreviewModal = ({
  isOpen,
  onClose,
  overallScore,
  scoreBadgeClass,
  scoreLabel,
  policyTopics,
  activeDocTab,
  setActiveDocTab,
  formatTopicKey,
  tabDropdownOpen,
  setTabDropdownOpen,
  suggestedOpen,
  setSuggestedOpen,
  issuesOpen,
  setIssuesOpen,
  suggestedItems,
  checkedFeedbacks,
  setCheckedFeedbacks,
  answerInputs,
  setAnswerInputs,
  handleApplyFeedbacks,
  isApplying,
  handleIgnoreFeedbacks,
  isIgnoring,
  handleApplyIssueFeedbacks,
  isApplyingIssues,
  handleIgnoreIssueFeedbacks,
  isIgnoringIssues,
  issueItems,
  topicScore,
  topicSections,
  renderContent,
  onDownloadAll,
  onDownloadCurrentTopic,
  currentTopicLabel,
}) => {
  // Hooks must run before the early return below, so this stays above it.
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);

  // Seed each Dreams suggestion's proposed text into answerInputs, so Apply is
  // enabled by ticking the box alone. `canApplyIssues` requires a non-empty answer
  // per item, and the proposal IS the answer — without this the merchant would have
  // to retype text that is already correct just to enable the button. Only fills
  // keys that have no value yet, so it never overwrites an edit in progress.
  useEffect(() => {
    if (!isOpen) return;
    const seeds = {};
    for (const item of [...(issueItems || []), ...(suggestedItems || [])]) {
      if (!isDreamsSuggestion(item)) continue;
      const proposed = item.unclear || item.rule || "";
      if (proposed && answerInputs[item.key] === undefined) seeds[item.key] = proposed;
    }
    if (Object.keys(seeds).length) {
      setAnswerInputs(prev => ({ ...seeds, ...prev }));
    }
  }, [isOpen, issueItems, suggestedItems, answerInputs, setAnswerInputs]);

  if (!isOpen) return null;

  const canApplyIssues = issueItems.some(gap => checkedFeedbacks.has(gap.key) && (answerInputs[gap.key] || "").trim());
  // Ignore only needs a checked item — unlike Apply, it doesn't require an answer.
  const canIgnoreSuggested = suggestedItems.some(item => checkedFeedbacks.has(item.key));
  const canIgnoreIssues = issueItems.some(gap => checkedFeedbacks.has(gap.key));

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div
        className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-[95vw] flex flex-col overflow-hidden overflow-x-hidden"
        style={{ height: "90vh", maxHeight: "90vh" }}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-[16px] lg:px-[32px] py-[16px] border-b border-[#F0F0F0] flex-shrink-0 gap-2 min-w-0">
          <div className="flex items-center gap-2 lg:gap-4 min-w-0 flex-1">
            <span
              className="text-[#0C0D17] text-[22px] font-semibold font-dmsans truncate max-w-[130px] lg:max-w-none"
              title="Document Preview"
            >
              Document preview
            </span>
            {overallScore != null && (
              <span
                className={`text-[13px] font-medium font-dmsans px-3 py-1 rounded-full flex-shrink-0 whitespace-nowrap ${scoreBadgeClass}`}
              >
                {scoreLabel} {Math.round(overallScore)}/100
              </span>
            )}
          </div>
          {/* Download the categorized policies — all topics, or just the open one.
              Exports the same content rendered below, as Markdown. */}
          <div className="relative flex-shrink-0">
            <button
              data-testid="download-categorized-policies"
              type="button"
              onClick={() => setDownloadMenuOpen(v => !v)}
              title="Download categorized policies"
              className="flex items-center gap-1.5 border border-[#DDDDDD] rounded-md px-3 py-[10px] text-[#0C0D17] text-[14px] font-medium font-dmsans hover:bg-[#F5F5F5] transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 2.5v8M5 7.5l3 3 3-3M2.5 12.5h11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="hidden lg:inline">Download</span>
            </button>
            {downloadMenuOpen && (
              <>
                <div className="fixed inset-0" style={{ zIndex: 55 }} onClick={() => setDownloadMenuOpen(false)} />
                <div
                  className="absolute right-0 mt-1 w-[240px] bg-white border border-[#E5E7EB] rounded-md shadow-lg overflow-hidden"
                  style={{ top: "100%", zIndex: 56 }}
                >
                  <button
                    data-testid="download-all-topics"
                    type="button"
                    onClick={() => {
                      setDownloadMenuOpen(false);
                      onDownloadAll?.();
                    }}
                    className="w-full text-left px-3 py-2.5 text-[13px] font-medium font-dmsans text-[#0C0D17] hover:bg-[#F9FAFB] transition-colors"
                  >
                    All categorized policies
                  </button>
                  <button
                    data-testid="download-current-topic"
                    type="button"
                    disabled={!currentTopicLabel}
                    onClick={() => {
                      setDownloadMenuOpen(false);
                      onDownloadCurrentTopic?.();
                    }}
                    className="w-full text-left px-3 py-2.5 text-[13px] font-medium font-dmsans text-[#0C0D17] hover:bg-[#F9FAFB] transition-colors disabled:text-[#9CA3AF] disabled:hover:bg-white border-t border-[#F0F0F0]"
                  >
                    <span className="block truncate">{currentTopicLabel || "Current topic"}</span>
                    <span className="block text-[11px] font-normal text-[#868CA2]">This topic only</span>
                  </button>
                </div>
              </>
            )}
          </div>
          {/* Cancel button — desktop only */}
          <button
            className="hidden lg:block text-[#0C0D17] text-[14px] font-medium font-dmsans hover:opacity-60 transition-opacity border p-[10px] rounded-md flex-shrink-0"
            onClick={onClose}
          >
            Cancel
          </button>
          {/* × close icon — mobile and tablet */}
          <button
            className="lg:hidden flex-shrink-0 text-[#0C0D17] hover:opacity-60 transition-opacity p-[6px]"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Tab bar — desktop only */}
        <div className="hidden lg:flex border-b border-[#E5E7EB] px-[24px] pt-[8px] flex-shrink-0 bg-[#F9FAFB]">
          {policyTopics.map((topic, idx) => (
            <button
              key={topic.key}
              onClick={() => setActiveDocTab(idx)}
              className={`py-[10px] px-[24px] text-[13px] font-medium font-dmsans border-b-2 -mb-px whitespace-nowrap transition-colors ${
                activeDocTab === idx
                  ? "border-[#39F6A3] text-[#0C0D17]"
                  : "border-transparent text-[#868CA2] hover:text-[#0C0D17]"
              }`}
            >
              {formatTopicKey(topic.key)}
            </button>
          ))}
        </div>

        {/* Document selector — mobile and tablet */}
        <div className="lg:hidden border-b border-[#E5E7EB] px-[16px] py-[12px] flex-shrink-0 bg-[#F9FAFB]">
          <div className="relative">
            <button
              type="button"
              onClick={() => setTabDropdownOpen(v => !v)}
              className="w-full flex items-center justify-between border border-[#E5E7EB] rounded-md px-3 py-2 text-[13px] font-medium font-dmsans text-[#0C0D17] bg-white focus:outline-none"
            >
              <span className="truncate mr-2">
                {policyTopics[activeDocTab] ? formatTopicKey(policyTopics[activeDocTab].key) : "—"}
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className={`flex-shrink-0 transition-transform duration-200 ${tabDropdownOpen ? "rotate-180" : ""}`}
              >
                <path
                  d="M4 6l4 4 4-4"
                  stroke="#868CA2"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {tabDropdownOpen && (
              <>
                <div className="fixed inset-0" style={{ zIndex: 55 }} onClick={() => setTabDropdownOpen(false)} />
                <div
                  className="absolute left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-md shadow-lg overflow-hidden"
                  style={{ top: "100%", zIndex: 56 }}
                >
                  {policyTopics.map((topic, idx) => (
                    <button
                      key={topic.key}
                      type="button"
                      onClick={() => {
                        setActiveDocTab(idx);
                        setTabDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-[13px] font-medium font-dmsans transition-colors ${
                        activeDocTab === idx
                          ? "bg-[#F0FDF4] text-[#0C0D17]"
                          : "text-[#868CA2] hover:bg-[#F9FAFB] hover:text-[#0C0D17]"
                      }`}
                    >
                      {formatTopicKey(topic.key)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden p-[16px] lg:p-[24px] bg-[#F9FAFB] gap-[16px] lg:gap-[24px]">
          {/* Left sidebar */}
          <div
            className="flex flex-col gap-3 w-full lg:w-[var(--sidebar-w)] lg:flex-shrink-0 lg:overflow-y-auto transition-all duration-300"
            style={{ "--sidebar-w": `${suggestedOpen || issuesOpen ? 580 : 280}px` }}
          >
            {/* Suggested Improvements card */}
            <div className="bg-[#F6FFFB] shadow-md rounded-xl border border-[#E5E7EB] flex-shrink-0">
              <button
                className="w-full flex items-center justify-between px-[20px] py-[8px]"
                onClick={() => setSuggestedOpen(v => !v)}
              >
                <div className="text-left">
                  <p className="text-[#1DB87B] text-[15px] font-semibold font-dmsans">Policy gaps</p>
                  <p className="text-[#868CA2] text-[11px] font-normal font-dmsans mt-0.5 leading-relaxed">
                    These are the policy gaps identified according to the standard policy template.
                  </p>
                  <p className="text-[#868CA2] text-[12px] font-normal font-dmsans mt-1">
                    {suggestedItems.length} suggestions
                  </p>
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={`flex-shrink-0 transition-transform duration-200 ${suggestedOpen ? "rotate-180" : ""}`}
                >
                  <path
                    d="M4 6l4 4 4-4"
                    stroke="#868CA2"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {suggestedOpen && (
                <div className="border-t border-[#F0F0F0] bg-white">
                  <div className="p-[16px] flex flex-col gap-4">
                    {suggestedItems.length === 0 ? (
                      <p className="text-[#868CA2] text-[13px] font-dmsans">No suggestions for this topic.</p>
                    ) : (
                      suggestedItems.map((item, i) => {
                        const isChecked = checkedFeedbacks.has(item.key);
                        return (
                          <div
                            key={item.id || item.key || i}
                            className="flex items-start gap-3 cursor-pointer"
                            onClick={() =>
                              setCheckedFeedbacks(prev => {
                                const next = new Set(prev);
                                if (next.has(item.key)) next.delete(item.key);
                                else next.add(item.key);
                                return next;
                              })
                            }
                          >
                            <div
                              className={`w-4 h-4 mt-0.5 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${isChecked ? "bg-[#1DB87B] border-[#1DB87B]" : "bg-white border-[#D1D5DB]"}`}
                            >
                              {isChecked && (
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                  <path
                                    d="M1 4l2.5 2.5L9 1"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              {/* A Dreams suggestion reads as four things in order:
                                  the rule the agents keep applying, what the policy
                                  says today, how often they applied it, and the one
                                  action to take. A scorer gap has none of those and
                                  keeps the original label/evidence rendering. */}
                              {isDreamsSuggestion(item) ? (
                                <>
                                  {/* Badge only the contradiction — see the Issues tab
                                      below for why an "add" carries none. */}
                                  <div className="flex items-start gap-2">
                                    {actionOf(item) === "update" && (
                                      <span className="mt-0.5 flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold font-dmsans uppercase tracking-wide bg-[#FEF3C7] text-[#B45309]">
                                        conflict
                                      </span>
                                    )}
                                    <p className="text-[#0C0D17] text-[14px] font-medium font-dmsans leading-snug">
                                      {item.label}
                                    </p>
                                  </div>

                                  {/* Same two halves as the Issues tab below: what your
                                      team does (rule + count), then what your policy says
                                      (quote + how it differs). The count sits with the
                                      rule because it is that rule's support. */}
                                  {item.rule && (
                                    <p className="text-[#0C0D17] text-[13px] font-dmsans mt-1.5 leading-relaxed">
                                      {item.rule}
                                    </p>
                                  )}

                                  {item.occurrences > 0 && (
                                    <p className="text-[#7C3AED] text-[12px] font-medium font-dmsans mt-1 leading-relaxed">
                                      Applied by your team in {item.occurrences}{" "}
                                      {item.occurrences === 1 ? "reply" : "replies"}
                                    </p>
                                  )}

                                  {item.evidence ? (
                                    <div className="mt-2 border-l-2 border-[#FCD34D] pl-2.5">
                                      <p className="text-[#868CA2] text-[11px] font-medium font-dmsans uppercase tracking-wide">
                                        Your policy currently says
                                      </p>
                                      <p className="text-[#4B5563] text-[13px] font-dmsans mt-0.5 leading-relaxed italic">
                                        {item.evidence}
                                      </p>
                                      {item.detail && (
                                        <p className="text-[#B45309] text-[12px] font-dmsans mt-1 leading-relaxed">
                                          {item.detail}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-[#868CA2] text-[12px] font-dmsans mt-2 leading-relaxed">
                                      Your policy doesn&apos;t mention this yet.
                                    </p>
                                  )}

                                  {/* The ask: the text that goes INTO the policy,
                                      pre-filled and editable. Checking the box is
                                      the merchant's approval, so typing is optional
                                      — they only edit when the wording is wrong. */}
                                  <div className="mt-2" onClick={e => e.stopPropagation()}>
                                    <p className="text-[#4B5563] text-[12px] font-medium font-dmsans mb-1">
                                      {actionOf(item) === "update" ? "Replace it with" : "Add to your policy"}
                                    </p>
                                    <textarea
                                      rows={2}
                                      value={
                                        answerInputs[item.key] !== undefined
                                          ? answerInputs[item.key]
                                          : item.unclear || item.rule || ""
                                      }
                                      onChange={e => {
                                        const val = e.target.value;
                                        setAnswerInputs(prev => ({ ...prev, [item.key]: val }));
                                        setCheckedFeedbacks(prev => {
                                          const next = new Set(prev);
                                          if (val.trim()) next.add(item.key);
                                          return next;
                                        });
                                      }}
                                      className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-[13px] font-dmsans leading-relaxed resize-y focus:outline-none focus:border-[#1DB87B]"
                                    />
                                  </div>
                                </>
                              ) : (
                                <>
                                  <p className="text-[#0C0D17] text-[14px] font-medium font-dmsans leading-snug">
                                    {item.label}
                                  </p>
                                  {item.evidence && (
                                    <p className="text-[#868CA2] text-[14px] font-normal font-dmsans mt-1 leading-relaxed">
                                      {item.evidence}
                                    </p>
                                  )}
                                  {/* The two values that disagree. Without these the
                                      question ("which is correct?") has no referent. */}
                                  {item.conflict && (
                                    <p className="text-[#B45309] text-[13px] font-dmsans mt-1 leading-relaxed">
                                      {item.conflict}
                                    </p>
                                  )}
                                  {item.unclear && (
                                    <p className="text-[#B45309] text-[13px] font-dmsans mt-1 leading-relaxed">
                                      {item.unclear}
                                    </p>
                                  )}
                                </>
                              )}
                              {!isDreamsSuggestion(item) && item.question && (
                                <div className="mt-2" onClick={e => e.stopPropagation()}>
                                  <p className="text-[#4B5563] text-[13px] font-dmsans mb-1">{item.question}</p>
                                  <input
                                    type="text"
                                    value={answerInputs[item.key] || ""}
                                    placeholder="Type your answer…"
                                    onChange={e => {
                                      const val = e.target.value;
                                      setAnswerInputs(prev => ({ ...prev, [item.key]: val }));
                                      // Auto-check the item once an answer is typed.
                                      setCheckedFeedbacks(prev => {
                                        const next = new Set(prev);
                                        if (val.trim()) next.add(item.key);
                                        return next;
                                      });
                                    }}
                                    className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-[13px] font-dmsans focus:outline-none focus:border-[#1DB87B]"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="flex justify-end gap-2 px-[16px] pb-[16px]">
                    <button
                      onClick={handleIgnoreFeedbacks}
                      disabled={!canIgnoreSuggested || isIgnoring}
                      className="px-[16px] py-[10px] rounded-lg text-[13px] font-semibold font-dmsans transition-colors flex items-center gap-2 border"
                      style={{
                        borderColor: canIgnoreSuggested && !isIgnoring ? "#D1D5DB" : "#E5E7EB",
                        color: canIgnoreSuggested && !isIgnoring ? "#0C0D17" : "#9CA3AF",
                        cursor: canIgnoreSuggested && !isIgnoring ? "pointer" : "default",
                      }}
                    >
                      {isIgnoring && (
                        <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
                          <path d="M7 2 A5 5 0 0 1 12 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      )}
                      {isIgnoring ? "Ignoring..." : "Ignore"}
                    </button>
                    <button
                      onClick={handleApplyFeedbacks}
                      disabled={checkedFeedbacks.size === 0 || isApplying}
                      className="px-[16px] py-[10px] rounded-lg text-[13px] font-semibold font-dmsans transition-colors flex items-center gap-2"
                      style={{
                        backgroundColor: checkedFeedbacks.size > 0 && !isApplying ? "#0C0D17" : "#D1D5DB",
                        color: checkedFeedbacks.size > 0 && !isApplying ? "#39F6A3" : "#9CA3AF",
                        cursor: checkedFeedbacks.size > 0 && !isApplying ? "pointer" : "default",
                      }}
                    >
                      {isApplying && (
                        <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
                          <path d="M7 2 A5 5 0 0 1 12 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      )}
                      {isApplying ? "Applying..." : "Apply"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Issues & Questions card */}
            <div className="bg-[#FFF5F5] rounded-xl border border-[#FECACA] flex-shrink-0">
              <button
                className="w-full flex items-center justify-between px-[20px] py-[8px]"
                onClick={() => setIssuesOpen(v => !v)}
              >
                <div className="text-left">
                  <p className="text-[#82181A] text-[15px] font-semibold font-dmsans">Policy issues</p>
                  <p className="text-[#868CA2] text-[11px] font-normal font-dmsans mt-0.5 leading-relaxed">
                    These are changes suggested based on edits observed in generated drafts
                  </p>
                  <p className="text-[#C10007] text-[12px] font-normal font-dmsans mt-1">
                    {issueItems.length} {issueItems.length === 1 ? "issue" : "issues"} require answers
                  </p>
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={`flex-shrink-0 transition-transform duration-200 ${issuesOpen ? "rotate-180" : ""}`}
                >
                  <path
                    d="M4 6l4 4 4-4"
                    stroke="#868CA2"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {issuesOpen && (
                <div className="border-t border-[#FECACA] bg-white">
                  <div className="p-[16px] flex flex-col gap-3">
                    {issueItems.length === 0 ? (
                      <p className="text-[#868CA2] text-[13px] font-dmsans">No issues for this topic.</p>
                    ) : (
                      issueItems.map((gap, i) => {
                        const isChecked = checkedFeedbacks.has(gap.key);
                        return (
                          <div
                            key={gap.id || gap.key || i}
                            className="flex items-start gap-3 p-[16px] border border-[#FECACA] rounded-md cursor-pointer"
                            onClick={() =>
                              setCheckedFeedbacks(prev => {
                                const next = new Set(prev);
                                if (next.has(gap.key)) next.delete(gap.key);
                                else next.add(gap.key);
                                return next;
                              })
                            }
                          >
                            <div
                              className={`w-4 h-4 mt-0.5 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${isChecked ? "bg-[#C10007] border-[#C10007]" : "bg-white border-[#D1D5DB]"}`}
                            >
                              {isChecked && (
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                  <path
                                    d="M1 4l2.5 2.5L9 1"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              {isDreamsSuggestion(gap) ? (
                                /* Four things, in order: the rule the agents keep
                                   applying, what the policy says today, how often
                                   they applied it, and the one action to take.
                                   Everything else that used to render here — the
                                   conflict line twice over, the restated question,
                                   the always-true "Required" — said the same thing
                                   again in different words. */
                                <>
                                  {/* Only the CONTRADICTION is badged. An "add" is the
                                      common case (9 of 11 items on a typical store), so
                                      badging it marks almost everything and distinguishes
                                      nothing — and the action is already named on the
                                      "Add to your policy" label above the textarea. Green
                                      was actively misleading: in this UI it reads as
                                      resolved, while an add is outstanding work. */}
                                  <div className="flex items-start gap-2">
                                    {actionOf(gap) === "update" && (
                                      <span className="mt-0.5 flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold font-dmsans uppercase tracking-wide bg-[#FEF3C7] text-[#B45309]">
                                        conflict
                                      </span>
                                    )}
                                    <p className="text-[#0C0D17] text-[13px] font-medium font-dmsans leading-snug">
                                      {gap.label}
                                    </p>
                                  </div>

                                  {/* Reads as two halves, in the order the merchant needs
                                      to make the decision:

                                        WHAT YOUR TEAM DOES   the rule + how many replies
                                        WHAT YOUR POLICY SAYS the quoted line + how it differs

                                      The count belongs to the FIRST half: it is the support
                                      for the pattern ("74 replies did this"), not a count of
                                      the conflict. It used to sit after the policy quote,
                                      where it read as if it were counting the disagreement. */}
                                  {gap.rule && (
                                    <p className="text-[#0C0D17] text-[13px] font-dmsans mt-1.5 leading-relaxed">
                                      {gap.rule}
                                    </p>
                                  )}

                                  {gap.occurrences > 0 && (
                                    <p className="text-[#7C3AED] text-[12px] font-medium font-dmsans mt-1 leading-relaxed">
                                      Applied by your team in {gap.occurrences}{" "}
                                      {gap.occurrences === 1 ? "reply" : "replies"}
                                    </p>
                                  )}

                                  {gap.evidence ? (
                                    <div className="mt-2 border-l-2 border-[#FCD34D] pl-2.5">
                                      <p className="text-[#868CA2] text-[11px] font-medium font-dmsans uppercase tracking-wide">
                                        Your policy currently says
                                      </p>
                                      <p className="text-[#4B5563] text-[13px] font-dmsans mt-0.5 leading-relaxed italic">
                                        {gap.evidence}
                                      </p>
                                      {gap.detail && (
                                        <p className="text-[#B45309] text-[12px] font-dmsans mt-1 leading-relaxed">
                                          {gap.detail}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    /* An `add` has no quote, and saying nothing left the
                                       merchant to infer why it is here. State the finding
                                       explicitly — it is the reason the rule needs adding. */
                                    <p className="text-[#868CA2] text-[12px] font-dmsans mt-2 leading-relaxed">
                                      Your policy doesn&apos;t mention this yet.
                                    </p>
                                  )}

                                  {/* The text that goes INTO the policy, pre-filled
                                      and editable. Seeded into answerInputs on mount
                                      (see useEffect) so Apply is enabled by checking
                                      the box — the merchant types only to correct it. */}
                                  <div className="mt-2" onClick={e => e.stopPropagation()}>
                                    <p className="text-[#4B5563] text-[12px] font-medium font-dmsans mb-1">
                                      {actionOf(gap) === "update" ? "Replace it with" : "Add to your policy"}
                                    </p>
                                    <textarea
                                      rows={2}
                                      value={
                                        answerInputs[gap.key] !== undefined
                                          ? answerInputs[gap.key]
                                          : gap.unclear || gap.rule || ""
                                      }
                                      onChange={e => {
                                        const val = e.target.value;
                                        setAnswerInputs(prev => ({ ...prev, [gap.key]: val }));
                                        setCheckedFeedbacks(prev => {
                                          const next = new Set(prev);
                                          if (val.trim()) next.add(gap.key);
                                          return next;
                                        });
                                      }}
                                      className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-[13px] font-dmsans leading-relaxed resize-y focus:outline-none focus:border-[#1DB87B]"
                                    />
                                  </div>
                                </>
                              ) : (
                                <>
                                  <p className="text-[#0C0D17] text-[13px] font-medium font-dmsans leading-snug">
                                    {gap.label}
                                  </p>
                                  {gap.evidence && (
                                    <p className="text-[#868CA2] text-[13px] font-normal font-dmsans mt-1 leading-relaxed">
                                      {gap.evidence}
                                    </p>
                                  )}
                                  {/* A self-contradiction: the policy answers this same point two
                                    different ways. Called out separately from evidence because the
                                    merchant needs to see WHAT disagrees, not just be asked to restate
                                    the rule. */}
                                  {gap.conflict && (
                                    <p className="text-[#B91C1C] text-[12px] font-medium font-dmsans mt-1.5 leading-relaxed bg-[#FEF2F2] border border-[#FECACA] rounded px-2 py-1.5">
                                      <span className="font-semibold">Conflicting values: </span>
                                      {gap.conflict}
                                    </p>
                                  )}
                                  <p className="text-[#868CA2] text-[12px] font-normal font-dmsans mt-1 leading-relaxed">
                                    {gap.required ? "Required — needs improvement" : "Optional — needs improvement"}
                                  </p>
                                </>
                              )}
                              {!isDreamsSuggestion(gap) && gap.question && (
                                <div className="mt-2" onClick={e => e.stopPropagation()}>
                                  <p className="text-[#4B5563] text-[13px] font-dmsans mb-1">{gap.question}</p>
                                  <input
                                    type="text"
                                    value={answerInputs[gap.key] || ""}
                                    placeholder="Type your answer…"
                                    onChange={e => {
                                      const val = e.target.value;
                                      setAnswerInputs(prev => ({ ...prev, [gap.key]: val }));
                                      // Auto-check the item once an answer is typed.
                                      setCheckedFeedbacks(prev => {
                                        const next = new Set(prev);
                                        if (val.trim()) next.add(gap.key);
                                        return next;
                                      });
                                    }}
                                    className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-[13px] font-dmsans focus:outline-none focus:border-[#1DB87B]"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {issueItems.length > 0 && (
                    <div className="flex justify-end gap-2 px-[16px] pb-[16px]">
                      <button
                        onClick={handleIgnoreIssueFeedbacks}
                        disabled={!canIgnoreIssues || isIgnoringIssues}
                        className="px-[16px] py-[10px] rounded-lg text-[13px] font-semibold font-dmsans transition-colors flex items-center gap-2 border"
                        style={{
                          borderColor: canIgnoreIssues && !isIgnoringIssues ? "#D1D5DB" : "#E5E7EB",
                          color: canIgnoreIssues && !isIgnoringIssues ? "#0C0D17" : "#9CA3AF",
                          cursor: canIgnoreIssues && !isIgnoringIssues ? "pointer" : "default",
                        }}
                      >
                        {isIgnoringIssues && (
                          <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
                            <path
                              d="M7 2 A5 5 0 0 1 12 7"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                        {isIgnoringIssues ? "Ignoring..." : "Ignore"}
                      </button>
                      <button
                        onClick={handleApplyIssueFeedbacks}
                        disabled={!canApplyIssues || isApplyingIssues}
                        className="px-[16px] py-[10px] rounded-lg text-[13px] font-semibold font-dmsans transition-colors flex items-center gap-2"
                        style={{
                          backgroundColor: canApplyIssues && !isApplyingIssues ? "#0C0D17" : "#D1D5DB",
                          color: canApplyIssues && !isApplyingIssues ? "#39F6A3" : "#9CA3AF",
                          cursor: canApplyIssues && !isApplyingIssues ? "pointer" : "default",
                        }}
                      >
                        {isApplyingIssues && (
                          <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
                            <path
                              d="M7 2 A5 5 0 0 1 12 7"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                        {isApplyingIssues ? "Applying..." : "Apply"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right content card */}
          <div className="flex-1 bg-white rounded-xl border border-[#E5E7EB] flex flex-col overflow-hidden">
            {/* Score row */}
            <div className="bg-[#F9FAFB] flex flex-wrap lg:flex-nowrap items-center gap-2 px-[16px] lg:px-[24px] py-[12px] border-b border-[#F0F0F0] flex-shrink-0">
              <img src={FileReviewIcon} alt="document" style={{ width: 20, height: 25, objectFit: "contain" }} />
              <span className="text-[#0C0D17] text-[15px] font-medium font-dmsans">Score</span>
              <span className="text-[#D97706] text-[14px] font-semibold font-dmsans">
                {topicScore != null ? `${topicScore}/100` : "--"}
              </span>
              <div className="relative flex-shrink-0 w-full lg:w-[244px]" style={{ height: 4 }}>
                <div className="absolute inset-0 rounded-full" style={{ backgroundColor: "rgba(11,12,22,0.09)" }} />
                <div
                  className="absolute left-0 top-0 h-full rounded-full bg-[#D97706]"
                  style={{ width: topicScore != null ? `${(topicScore / 100) * 244}px` : 0 }}
                />
              </div>
            </div>

            {/* Document content */}
            <div className="flex-1 overflow-y-auto p-[16px] lg:p-[32px] font-dmsans">
              {topicSections.length === 0 ? (
                <p className="text-[#868CA2] text-[13px]">No content available for this topic.</p>
              ) : (
                topicSections.map((section, si) => (
                  <div key={section.section_key} className={si > 0 ? "mt-8" : ""}>
                    <h2 className="text-[16px] font-bold text-[#0C0D17] mb-3">{section.section_key}</h2>
                    {section.content ? (
                      <div className="space-y-1">{renderContent(section.content)}</div>
                    ) : (
                      <p className="text-[#868CA2] text-[13px]">No content available for this topic.</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
